import axios from 'axios';
import { useQuery } from 'react-query';
import { useAllMyCourses } from './useAllMyCourses';
import { useCurrentUser } from './useCurrentUser';
import getTimeDenomination, { DENOMINATION } from 'utils/getTimeDenomination';

type CourseKnowledgeBankItem = {
  id: number;
  name: string;
  distanceToGoal: number;
  sinceLastStudy: number;
  lastStudyDenom: DENOMINATION;
  totalStudyTimeMillis: number;
  totalStudyDenom: DENOMINATION;
  goodForNow: number;
  lastStudyTime: number;
  totalStudyTime: number;
  earliestStudyTime: string | null;
  nextAt: number;
  nextAtDenom: DENOMINATION;
  itemCount: {
    secure: number;
    fading: number;
    notSeen: number;
    total: number;
    securePercent: string;
    fadingPercent: string;
  };
};

type UseAllCoursesKnowledgeBankData = {
  data: CourseKnowledgeBankItem[];
  isLoading: boolean;
};

type CoursesResponse = {
  data: Array<{
    id: string;
    type: string;
    attributes: {
      name: string;
    };
    meta: {
      progress: number;
      'last-study-time': string | null;
      'secure-items-count': number;
      'fading-items-count': number;
      'not-seen-items-count': number;
      'total-items-count': number;
    };
  }>;
  included: any[];
  links: Record<string, any>;
  meta: {
    'total-count': number;
    'payment-required-course-ids': number[];
  };
};

const fetchCourseKnowledgeBank = async (userId: string, courseId: string) => {
  try {
    // Get assignments using the v4 endpoint
    const assignmentsResponse = await axios.get(`/api/v4/courses/${courseId}/assignments?include=image&page[size]=200`);

    const assignments = assignmentsResponse.data.data || [];

    if (!assignments.length) {
      return {
        earliestStudyTime: null,
        memoryAggregate: null,
      };
    }

    // Get memory aggregate data for the course using the first assignment
    // This is a limitation - ideally we'd aggregate across all assignments
    const memoryAggregateResponse = await axios.get(
      `/api/v2/users/${userId}/sets/${assignments[0].id}/aggregate_memory_bank`
    );

    const memoryAggregate = memoryAggregateResponse.data;

    // Find the earliest see-next-at time across all assignments
    const seeNextTimes = assignments
      .map((assignment) => assignment.meta?.['memory-aggregate']?.['see-next-at'])
      .filter((time): time is string => !!time);

    let earliestStudyTime = null;
    if (seeNextTimes.length > 0) {
      // Sort by actual date values, not strings
      const sortedTimes = seeNextTimes.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
      earliestStudyTime = sortedTimes[0];
    }

    return {
      earliestStudyTime,
      memoryAggregate,
    };
  } catch (error: any) {
    console.error(`Error fetching knowledge bank for course ${courseId}:`, error.response?.data || error);
    return {
      earliestStudyTime: null,
      memoryAggregate: null,
    };
  }
};

export const useAllCoursesKnowledgeBank = (): UseAllCoursesKnowledgeBankData => {
  const currentUser = useCurrentUser();
  const loggedInUserId = currentUser?.id;

  const { data: coursesResponse, isLoading: isLoadingCourses } = useAllMyCourses() as {
    data: CoursesResponse | undefined;
    isLoading: boolean;
  };

  // Use raw data if normalization fails
  const courses = coursesResponse?.data || [];

  const { data: knowledgeBankData, isLoading: isLoadingKnowledgeBank } = useQuery(
    ['coursesKnowledgeBank', loggedInUserId, courses.map((c) => c.id)],
    async () => {
      if (!loggedInUserId) return {};
      const promises = courses.map((course) => fetchCourseKnowledgeBank(loggedInUserId, course.id));
      const results = await Promise.all(promises);
      return Object.fromEntries(courses.map((course, i) => [course.id, results[i]]));
    },
    {
      enabled: !!loggedInUserId && courses.length > 0,
    }
  );

  const isLoading = isLoadingCourses || isLoadingKnowledgeBank;

  const data: CourseKnowledgeBankItem[] = courses
    .map((course): CourseKnowledgeBankItem | null => {
      if (!course?.meta) return null;

      const now = Date.now();
      const time = (t: string) => new Date(t).getTime();
      const convertToTimeSince = (d: string) => now - time(d);

      const lastStudyTime = course.meta['last-study-time'] ? convertToTimeSince(course.meta['last-study-time']) : 0;
      const [lastStudyDenom, convertLastStudyToDenom] = getTimeDenomination(lastStudyTime);

      const courseKnowledgeBank = knowledgeBankData?.[course.id];
      const memoryAggregate = courseKnowledgeBank?.memoryAggregate?.response?.[0];

      // Calculate next study time
      const earliestStudyTime = courseKnowledgeBank?.earliestStudyTime || null;
      const nextStudyTime = earliestStudyTime ? new Date(earliestStudyTime).getTime() - now : 0;
      const [nextAtDenom, convertNextStudyToDenom] = getTimeDenomination(Math.max(0, nextStudyTime));
      const nextAt = convertNextStudyToDenom(Math.max(0, nextStudyTime));

      // Get total study time from memory aggregate data
      const totalStudyTime = memoryAggregate?.total_study_time_millis || 0;
      const [totalStudyDenom, convertTotalStudyToDenom] = getTimeDenomination(totalStudyTime);

      const itemCount = {
        secure: memoryAggregate?.items_in_level?.['1'] || 0,
        fading: memoryAggregate?.items_in_level?.['0'] || 0,
        notSeen: memoryAggregate?.items_in_level?.['new'] || 0,
        total: memoryAggregate?.items_count || 0,
        securePercent: memoryAggregate?.items_count
          ? `${(100 / memoryAggregate.items_count) * (memoryAggregate.items_in_level?.['1'] || 0)}%`
          : '0%',
        fadingPercent: memoryAggregate?.items_count
          ? `${(100 / memoryAggregate.items_count) * (memoryAggregate.items_in_level?.['0'] || 0)}%`
          : '0%',
      };

      return {
        id: parseInt(course.id, 10),
        name: course.attributes.name || course.id,
        distanceToGoal: course.meta.progress * 100, // Convert to percentage
        sinceLastStudy: convertLastStudyToDenom(lastStudyTime),
        lastStudyDenom,
        totalStudyTimeMillis: totalStudyTime, // Raw milliseconds from API
        totalStudyDenom,
        goodForNow: course.meta.progress * 100, // Use progress as mastery
        lastStudyTime,
        totalStudyTime: convertTotalStudyToDenom(totalStudyTime), // Converted value for display
        earliestStudyTime,
        nextAt,
        nextAtDenom,
        itemCount,
      };
    })
    .filter((item): item is CourseKnowledgeBankItem => item !== null);

  return { data, isLoading };
};
